import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocalizedUrl } from 'tradera-utils/url';

/**
 * i18next translation wrappers for translating static text in react components
 * When to use what?
 * -----
 * 1. For functional components use `useTranslator()` hook
 * 2. For class components use `withTranslator()` HOC
 */

/**
 * Translation hook, wrapper for react-i18next
 * @return {{t: Function}}
 */
export const useTranslator = () => {
    const { t, ready } = useTranslation(undefined, { wait: true });
    return { t: ready ? t : () => '...' };
};

/**
 * Translation HOC, wrapper for react-i18next
 * @param {Component} WrappedComponent
 * @return {Component} The wrapped component.
 */
export function withTranslator(WrappedComponent) {
    function WithTranslator(props) {
        const { t } = useTranslator();
        return <WrappedComponent t={t} {...props} />;
    }

    WithTranslator.displayName = `withTranslator(${WrappedComponent.displayName ||
        WrappedComponent.name}`;
    return WithTranslator;
}

/**
 * Localization HOC, wrapper for URL-localizer
 * @param {Component} WrappedComponent
 */
export function withUrlLocalizer(WrappedComponent) {
    function WithUrlLocalizer(props) {
        const { toLocalizedUrl } = useUrlLocalizer();
        return <WrappedComponent toLocalizedUrl={toLocalizedUrl} {...props} />;
    }

    WithUrlLocalizer.displayName = `withUrlLocalizer(${WrappedComponent.displayName ||
        WrappedComponent.name}`;

    return WithUrlLocalizer;
}

/**
 * Current language hook, wrapper for react-i18next
 * @return {{language: string}}
 */
export const useCurrentLanguage = () => {
    const { i18n, ready } = useTranslation(undefined, { wait: true });
    return {
        language: ready ? i18n.language : null
    };
};

/**
 * Returns function to append language prefix to link
 * @return {{toLocalizedUrl: function}}
 */
export const useUrlLocalizer = () => {
    const { i18n, ready } = useTranslation(undefined, { wait: true });
    const urlLocalizer = useRef(url => toLocalizedUrl(url, i18n.language));
    return { toLocalizedUrl: ready ? urlLocalizer.current : url => url };
};
